import { useState } from "react";

import { ai as aiApi } from "../api/resources";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  PageHeader,
  Progress,
  Stat,
  StatusBadge,
} from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { useAsync } from "../lib/useAsync";
import type { AiProvider } from "../lib/types";

const BUDGET_TONE: Record<string, "ok" | "warn" | "danger" | undefined> = {
  ok: "ok",
  warning: "warn",
  exhausted: "danger",
  unlimited: undefined,
};

function money(amount: string | null | undefined, currency = "USD"): string {
  if (amount === null || amount === undefined) return "—";
  const value = Number(amount);
  if (Number.isNaN(value)) return String(amount);
  return `${currency === "USD" ? "$" : `${currency} `}${value.toFixed(value < 1 ? 4 : 2)}`;
}

export function AiSettingsPage() {
  const { organization, organizationId } = useCurrentOrganization();
  const { can } = useAuth();
  const [nonce, setNonce] = useState(0);
  const [editing, setEditing] = useState<AiProvider | "new" | undefined>();
  const [error, setError] = useState<string | undefined>();

  const providers = useAsync(
    () => (organizationId ? aiApi.providers(organizationId) : Promise.resolve([])),
    [organizationId, nonce],
  );
  const summary = useAsync(
    () => (organizationId ? aiApi.summary(organizationId) : Promise.resolve(undefined as never)),
    [organizationId, nonce],
  );
  const events = useAsync(
    () =>
      organizationId
        ? aiApi.usage(organizationId, { limit: 25 })
        : Promise.resolve({ items: [], total: 0, limit: 25, offset: 0 }),
    [organizationId, nonce],
  );
  const overview = useAsync(
    () => (organizationId ? aiApi.overview(organizationId) : Promise.resolve(undefined as never)),
    [organizationId, nonce],
  );
  const health = useAsync(
    () => (organizationId ? aiApi.health(organizationId) : Promise.resolve(undefined as never)),
    [organizationId, nonce],
  );

  const mayConfigure = can(organizationId, "ai:write");

  if (!organizationId || !organization) {
    return (
      <>
        <PageHeader title="AI & cost" />
        <Card>
          <EmptyState title="No organization selected" />
        </Card>
      </>
    );
  }

  const spend = summary.data;

  return (
    <>
      <PageHeader
        title="AI & cost"
        description="Which model would be used, what it costs, what it is allowed to cost, and what it has cost so far."
        actions={
          mayConfigure ? (
            <button className="primary" onClick={() => setEditing("new")}>
              Add provider
            </button>
          ) : null
        }
      />

      <div className="stack">
        <ErrorBanner error={error ?? providers.error} />

        <div className="banner banner--ok">
          Extraction is deterministic today — no model is called. This is the configuration, the
          budget and the meter that a model-backed extractor plugs into, so cost is visible from
          the first call rather than the first invoice.
        </div>

        <div className="grid grid--stats">
          <Stat
            label="Spend today"
            value={money(overview.data?.spend_today.cost_amount, overview.data?.spend_today.currency)}
            hint={overview.data?.spend_today.is_floor ? "a floor: some calls are unpriced" : undefined}
          />
          <Stat label="Spend this month" value={money(spend?.total_cost, spend?.currency)} />
          <Stat label="Calls" value={spend?.calls ?? 0} />
          <Stat label="Tokens" value={(spend?.total_tokens ?? 0).toLocaleString()} />
          <Stat
            label="Blocked by budget"
            value={spend?.blocked_calls ?? 0}
            tone={spend?.blocked_calls ? "warn" : undefined}
          />
          <Stat
            label="Conversations"
            value={overview.data?.conversations ?? 0}
          />
          <Stat
            label="Error rate"
            value={`${overview.data?.error_rate_percent ?? 0}%`}
            tone={(overview.data?.error_rate_percent ?? 0) > 5 ? "warn" : undefined}
          />
          <Stat
            label="Helpful ratings"
            value={
              overview.data?.feedback_positive_percent === null ||
              overview.data?.feedback_positive_percent === undefined
                ? "no ratings yet"
                : `${overview.data.feedback_positive_percent}%`
            }
            hint={
              overview.data?.feedback_count
                ? `${overview.data.feedback_count} rated`
                : "nobody has rated an answer"
            }
          />
          <Stat
            label="Trace coverage"
            value={`${overview.data?.trace.coverage_percent ?? 100}%`}
            tone={overview.data?.trace.healthy === false ? "warn" : undefined}
            hint={
              overview.data?.trace.healthy === false
                ? "work happened the ledger cannot account for"
                : undefined
            }
          />
          <Stat
            label="Unpriced calls"
            value={spend?.unpriced_events ?? 0}
            tone={spend?.unpriced_events ? "warn" : undefined}
            hint={spend?.unpriced_events ? "spend shown is a floor" : undefined}
          />
        </div>

        <Card title="Assistant health">
          <p className="small muted" style={{ margin: "0 0 8px" }}>
            What each dependency actually reports. Unavailable means unavailable — a check that
            could not run says so rather than showing green because nothing came back.
          </p>
          {health.loading ? <Loading /> : null}
          <ErrorBanner error={health.error} />
          {health.data ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Check</th>
                    <th>Status</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {health.data.checks.map((check) => (
                    <tr key={check.name}>
                      <td className="mono small">{check.name.replace(/_/g, " ")}</td>
                      <td>
                        <StatusBadge status={check.status} />
                      </td>
                      <td className="small">{check.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {overview.data && !overview.data.trace.healthy ? (
            <div className="banner banner--warn">
              <strong>Trace is incomplete.</strong> {overview.data.trace.missing_traces}{" "}
              conversation(s) did work with no ledger row,{" "}
              {overview.data.trace.missing_prompt_capture} call(s) captured no prompt, and{" "}
              {overview.data.trace.missing_tool_calls} used tools with nothing recorded.{" "}
              {overview.data.trace.note}
            </div>
          ) : null}
        </Card>

        <Card title="Providers" flush>
          {providers.loading ? (
            <Loading />
          ) : !providers.data?.length ? (
            <EmptyState
              title="No provider configured"
              description="Add one to set a model, its rates and a monthly ceiling. The API key itself is never stored here — only the name of the environment variable that holds it."
            />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Model</th>
                    <th>Rates / 1k</th>
                    <th>Budget</th>
                    <th>Key</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {providers.data.map((provider) => (
                    <tr key={provider.id}>
                      <td>
                        <strong>{provider.name}</strong>
                        <div className="tiny faint">
                          {provider.kind}
                          {provider.is_default ? " · default" : ""}
                          {provider.is_platform_default ? " · platform" : ""}
                          {!provider.is_active ? " · inactive" : ""}
                        </div>
                      </td>
                      <td className="mono small">{provider.model}</td>
                      <td className="small muted">
                        in {money(provider.input_cost_per_1k, provider.currency)} · out{" "}
                        {money(provider.output_cost_per_1k, provider.currency)}
                      </td>
                      <td>
                        {provider.monthly_budget ? (
                          <>
                            <div className="small">
                              {money(provider.month_to_date_cost, provider.currency)} of{" "}
                              {money(provider.monthly_budget, provider.currency)}
                            </div>
                            <Progress
                              value={
                                (Number(provider.month_to_date_cost ?? 0) /
                                  Number(provider.monthly_budget)) *
                                100
                              }
                            />
                          </>
                        ) : (
                          <span className="badge badge--neutral">no ceiling</span>
                        )}
                      </td>
                      <td>
                        {provider.credential_source === "dashboard" ? (
                          <>
                            <div className="mono tiny">{provider.api_key_hint ?? "stored"}</div>
                            <StatusBadge status="verified" label="in dashboard" />
                          </>
                        ) : provider.api_key_env_var ? (
                          <>
                            <div className="mono tiny">{provider.api_key_env_var}</div>
                            <StatusBadge
                              status={provider.api_key_present ? "verified" : "proposed"}
                              label={provider.api_key_present ? "from environment" : "not set"}
                            />
                          </>
                        ) : (
                          <StatusBadge status="proposed" label="no key" />
                        )}
                      </td>
                      <td>
                        {mayConfigure && !provider.is_platform_default ? (
                          <div className="row" style={{ gap: 4 }}>
                            <button className="btn--ghost" onClick={() => setEditing(provider)}>
                              Edit
                            </button>
                            <button
                              className="btn--ghost"
                              onClick={async () => {
                                setError(undefined);
                                try {
                                  await aiApi.removeProvider(organizationId, provider.id);
                                  setNonce((n) => n + 1);
                                } catch (cause) {
                                  setError(
                                    cause instanceof Error ? cause.message : String(cause),
                                  );
                                }
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="grid grid--two">
          <Card title="Spend by operation">
            {summary.loading ? (
              <Loading />
            ) : !spend?.by_operation.length ? (
              <p className="muted small" style={{ marginBottom: 0 }}>
                Nothing metered yet this month.
              </p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Operation</th>
                      <th>Calls</th>
                      <th>Tokens</th>
                      <th>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spend.by_operation.map((row) => (
                      <tr key={row.key}>
                        <td>{row.label ?? row.key}</td>
                        <td>{row.calls}</td>
                        <td>{row.total_tokens.toLocaleString()}</td>
                        <td>{money(row.cost_amount, spend.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="Budgets">
            {!spend?.budgets.length ? (
              <p className="muted small" style={{ marginBottom: 0 }}>
                No provider configured, so nothing to cap.
              </p>
            ) : (
              <div className="stack">
                {spend.budgets.map((budget) => (
                  <div key={budget.provider_id}>
                    <div className="row">
                      <strong>{budget.provider_name}</strong>
                      <div className="topbar__spacer" />
                      <Stat
                        label={budget.state}
                        value={money(budget.month_to_date_cost, budget.currency)}
                        tone={BUDGET_TONE[budget.state]}
                      />
                    </div>
                    {budget.percent_used !== null ? (
                      <Progress value={budget.percent_used} />
                    ) : null}
                    <div className="tiny faint">
                      {budget.monthly_budget
                        ? `${money(budget.remaining, budget.currency)} left of ${money(
                            budget.monthly_budget,
                            budget.currency,
                          )} this month`
                        : "No monthly ceiling set."}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <Card title="Recent calls" flush>
          {events.loading ? (
            <Loading />
          ) : !events.data?.items.length ? (
            <EmptyState
              title="No metered calls yet"
              description="Every call — including the ones refused for being over budget — is recorded here with the rates that applied at the time."
            />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Operation</th>
                    <th>Model</th>
                    <th>Tokens</th>
                    <th>Cost</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {events.data.items.map((event) => (
                    <tr key={event.id}>
                      <td className="small muted">
                        {event.created_at ? new Date(event.created_at).toLocaleString() : "—"}
                      </td>
                      <td>{event.operation}</td>
                      <td className="mono small">{event.model}</td>
                      <td className="small">
                        {event.total_tokens === null ? "unknown" : event.total_tokens.toLocaleString()}
                      </td>
                      <td>{event.priced ? money(event.cost_amount, event.currency) : "unpriced"}</td>
                      <td>
                        <StatusBadge status={event.status} />
                        {event.error ? <div className="tiny faint">{event.error}</div> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {editing ? (
        <ProviderModal
          organizationId={organizationId}
          provider={editing === "new" ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}

export type KeyAction = "keep" | "replace" | "clear";

/** Decide what the `api_key` field on a provider write should say — or whether it should be there.
 *
 * The three intentions are genuinely different and the API distinguishes them: an absent field
 * leaves the stored key alone, an empty string removes it, a value replaces it. The dangerous
 * case is "replace" with nothing typed, which must mean *leave it alone* rather than *delete it* —
 * opening the field and thinking better of it is not a request to lose a working credential.
 */
export function applyKeyAction(
  payload: Record<string, unknown>,
  action: KeyAction,
  typed: string,
): Record<string, unknown> {
  if (action === "clear") payload.api_key = "";
  else if (action === "replace" && typed) payload.api_key = typed;
  else delete payload.api_key;
  return payload;
}

/** What each vendor calls itself, and what to reach for first. Free text still wins — a model
 *  released after this build must be typeable. */
const KIND_LABELS: Record<string, string> = {
  anthropic: "Anthropic (Claude)",
  openai: "OpenAI",
  deepseek: "DeepSeek",
  other: "Other",
};

const DEFAULT_MODELS: Record<string, string> = {
  anthropic: "claude-sonnet-5",
  openai: "gpt-4o",
  deepseek: "deepseek-chat",
};

const KEY_PLACEHOLDERS: Record<string, string> = {
  anthropic: "sk-ant-…",
  openai: "sk-…",
  deepseek: "sk-…",
};

const ENV_VAR_PLACEHOLDERS: Record<string, string> = {
  anthropic: "ANTHROPIC_API_KEY",
  openai: "OPENAI_API_KEY",
  deepseek: "DEEPSEEK_API_KEY",
};

const VENDOR_HINTS: Record<string, string> = {
  deepseek: "Uses the OpenAI wire format against api.deepseek.com.",
};

function ProviderModal({
  organizationId,
  provider,
  onClose,
  onSaved,
}: {
  organizationId: string;
  provider?: AiProvider;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<Record<string, string | boolean>>({
    name: provider?.name ?? "",
    kind: provider?.kind ?? "anthropic",
    model: provider?.model ?? "",
    api_key: "",
    api_key_env_var: provider?.api_key_env_var ?? "",
    input_cost_per_1k: provider?.input_cost_per_1k ?? "0",
    output_cost_per_1k: provider?.output_cost_per_1k ?? "0",
    currency: provider?.currency ?? "USD",
    monthly_budget: provider?.monthly_budget ?? "",
    is_default: provider?.is_default ?? false,
    is_active: provider?.is_active ?? true,
  });
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  // A stored key is never sent back to the browser, so editing one is always a replacement, and
  // "leave it alone", "replace it" and "remove it" are three different intentions. An empty box
  // must not be able to mean the third by accident.
  const storedKey = provider?.credential_source === "dashboard";
  const [keyAction, setKeyAction] = useState<"keep" | "replace" | "clear">(
    storedKey ? "keep" : "replace",
  );

  const kinds = useAsync(() => aiApi.kinds(organizationId), [organizationId]);

  function set(key: string, value: string | boolean) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function save() {
    setBusy(true);
    setError(undefined);
    const payload: Record<string, unknown> = {
      ...form,
      api_key_env_var: form.api_key_env_var || null,
      monthly_budget: form.monthly_budget === "" ? null : form.monthly_budget,
    };
    applyKeyAction(payload, keyAction, String(form.api_key));
    try {
      if (provider) await aiApi.updateProvider(organizationId, provider.id, payload);
      else await aiApi.createProvider(organizationId, payload);
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={provider ? `Edit ${provider.name}` : "Add AI provider"}
      onClose={onClose}
      footer={
        <>
          <button className="btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="primary"
            disabled={busy || !form.name || !form.model}
            onClick={() => void save()}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <div className="grid grid--two">
        <Field label="Name">
          <input value={String(form.name)} onChange={(event) => set("name", event.target.value)} />
        </Field>
        <Field label="Kind" hint={VENDOR_HINTS[String(form.kind)]}>
          <select
            value={String(form.kind)}
            onChange={(event) => {
              const kind = event.target.value;
              set("kind", kind);
              // Changing vendor without changing model would send a Claude model name to DeepSeek.
              if (!form.model || Object.values(DEFAULT_MODELS).includes(String(form.model))) {
                set("model", DEFAULT_MODELS[kind] ?? "");
              }
            }}
          >
            {(kinds.data?.kinds ?? ["anthropic", "openai", "deepseek", "other"]).map((kind) => (
              <option key={kind} value={kind}>
                {KIND_LABELS[kind] ?? kind}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Model">
          <input
            value={String(form.model)}
            placeholder={DEFAULT_MODELS[String(form.kind)] ?? "claude-sonnet-5"}
            onChange={(event) => set("model", event.target.value)}
          />
        </Field>
        <Field
          label="API key"
          hint={
            keyAction === "replace"
              ? "Stored encrypted. It is never shown again, in this screen or any other."
              : keyAction === "clear"
                ? "The stored key will be removed when you save."
                : undefined
          }
        >
          {keyAction === "replace" ? (
            <>
              <input
                type="password"
                autoComplete="new-password"
                spellCheck={false}
                value={String(form.api_key)}
                placeholder={KEY_PLACEHOLDERS[String(form.kind)] ?? "sk-…"}
                onChange={(event) => set("api_key", event.target.value)}
              />
              {storedKey ? (
                <button
                  className="btn--ghost tiny"
                  type="button"
                  onClick={() => {
                    set("api_key", "");
                    setKeyAction("keep");
                  }}
                >
                  Keep the existing key
                </button>
              ) : null}
            </>
          ) : (
            <div className="row" style={{ gap: 8, alignItems: "center" }}>
              <span className="mono tiny">
                {keyAction === "clear" ? "will be removed" : (provider?.api_key_hint ?? "stored")}
              </span>
              <button
                className="btn--ghost tiny"
                type="button"
                onClick={() => {
                  set("api_key", "");
                  setKeyAction("replace");
                }}
              >
                Replace
              </button>
              <button
                className="btn--ghost tiny"
                type="button"
                onClick={() => setKeyAction(keyAction === "clear" ? "keep" : "clear")}
              >
                {keyAction === "clear" ? "Undo" : "Remove"}
              </button>
            </div>
          )}
        </Field>
        <Field
          label="Or read it from an environment variable"
          hint="The variable's NAME, not the key. A key entered above takes precedence."
        >
          <input
            value={String(form.api_key_env_var)}
            placeholder={ENV_VAR_PLACEHOLDERS[String(form.kind)] ?? "ANTHROPIC_API_KEY"}
            onChange={(event) => set("api_key_env_var", event.target.value)}
          />
        </Field>
        <Field label="Input cost per 1k tokens">
          <input
            value={String(form.input_cost_per_1k)}
            onChange={(event) => set("input_cost_per_1k", event.target.value)}
          />
        </Field>
        <Field label="Output cost per 1k tokens">
          <input
            value={String(form.output_cost_per_1k)}
            onChange={(event) => set("output_cost_per_1k", event.target.value)}
          />
        </Field>
        <Field label="Currency">
          <input
            value={String(form.currency)}
            onChange={(event) => set("currency", event.target.value)}
          />
        </Field>
        <Field
          label="Monthly budget"
          hint="Blank means no ceiling. Zero means configured but switched off."
        >
          <input
            value={String(form.monthly_budget)}
            onChange={(event) => set("monthly_budget", event.target.value)}
          />
        </Field>
      </div>
      <div className="row">
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={Boolean(form.is_default)}
            onChange={(event) => set("is_default", event.target.checked)}
          />
          Use this one by default
        </label>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={Boolean(form.is_active)}
            onChange={(event) => set("is_active", event.target.checked)}
          />
          Active
        </label>
      </div>
    </Modal>
  );
}
